import { Target, Gauge, TrendingUp, AlertTriangle } from "lucide-react";
import { pct } from "./flashTheme";

const Tile = ({ icon: Icon, label, value, sub, accent, testId, delay }) => (
  <div
    className="flash-rise bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-xl p-5 flex flex-col gap-2 hover:bg-white/[0.09] transition-colors"
    style={{ animationDelay: delay }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-400 font-heading">
      {label}
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className="font-mono text-3xl font-bold text-white tracking-tight">{value}</div>
    <div className="text-xs text-slate-400 truncate">{sub}</div>
  </div>
);

export const FlashHero = ({ data }) => {
  const s = data.summary;
  const mi = s.most_improved;
  const gap = s.biggest_gap;
  return (
    <section className="relative overflow-hidden rounded-2xl bg-[#0f172a] text-white p-6 sm:p-8" data-testid="hero-spotlight-section">
      <div className="absolute inset-0 flash-grid opacity-40 pointer-events-none" />
      <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-sky-500/20 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] text-sky-400 uppercase">Month-to-date spotlight</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2" data-testid="flash-month-label">
            {data.month_label}
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            Compared with {data.prior_month_label} close · {s.forms_reported} of {s.forms_total} forms reported
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Avg goal metric {pct(s.avg_on_time_pct)}</span>
          <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-rose-500" />{s.forms_below_goal} below goal</span>
        </div>
      </div>
      <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Target} label="Forms at goal" value={`${s.forms_at_goal} / ${s.forms_reported}`} sub="Meeting their goal" accent="text-sky-400" testId="hero-metric-goals-met" delay="40ms" />
        <Tile icon={Gauge} label="Avg goal metric" value={pct(s.avg_on_time_pct)} sub={`Across ${s.forms_reported} reported forms`} accent="text-emerald-400" testId="hero-metric-avg-ontime" delay="100ms" />
        <Tile icon={TrendingUp} label="Most improved" value={mi ? `${mi.delta > 0 ? "+" : ""}${mi.delta.toFixed(2)} pts` : "—"} sub={mi ? mi.name : `Enter ${data.prior_month_label} close to compare`} accent="text-sky-300" testId="hero-metric-biggest-improved" delay="160ms" />
        <Tile icon={AlertTriangle} label="Biggest gap to goal" value={gap ? `${gap.gap.toFixed(2)} pts` : s.forms_reported ? "None" : "—"} sub={gap ? `${gap.name}${gap.owner ? ` · ${gap.owner}` : ""}` : s.forms_reported ? "Every reported form meets goal" : "No forms reported yet"} accent="text-rose-400" testId="hero-metric-biggest-gap" delay="220ms" />
      </div>
    </section>
  );
};
